import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, TrendingUp, Users, Coins, DollarSign } from 'lucide-react';

interface NetworkAnalytics {
  chainId: number;
  networkName: string;
  symbol: string;
  totalTokens: number;
  tokensLast30Days: number;
  activeUsers: number;
  platformFee: string;
  revenue: string;
}

const NETWORKS = [
  { id: 0, name: 'All Networks', chainId: null },
  { id: 97, name: 'BSC Testnet', chainId: 97 },
  { id: 56, name: 'BSC Mainnet', chainId: 56 },
  { id: 1, name: 'Ethereum', chainId: 1 },
  { id: 11155111, name: 'Ethereum Sepolia', chainId: 11155111 },
  { id: 43113, name: 'Avalanche Fuji', chainId: 43113 },
  { id: 43114, name: 'Avalanche', chainId: 43114 },
  { id: 11124, name: 'Abstract Testnet', chainId: 11124 },
  { id: 2741, name: 'Abstract', chainId: 2741 },
  { id: 8453, name: 'Base', chainId: 8453 },
  { id: 84532, name: 'Base Sepolia', chainId: 84532 },
  { id: 10, name: 'OP Mainnet', chainId: 10 },
  { id: 11155420, name: 'OP Sepolia', chainId: 11155420 },
  { id: 42161, name: 'Arbitrum One', chainId: 42161 },
  { id: 421614, name: 'Arbitrum Sepolia', chainId: 421614 },
  { id: 137, name: 'Polygon', chainId: 137 },
  { id: 80002, name: 'Polygon Amoy', chainId: 80002 },
  { id: 1328, name: 'Sei Testnet', chainId: 1328 },
  { id: 1329, name: 'Sei', chainId: 1329 },
];

export function EnhancedBlockchainAnalytics() {
  const [selectedNetwork, setSelectedNetwork] = useState<number | null>(null);
  const [analytics, setAnalytics] = useState<NetworkAnalytics | null>(null);
  const [allNetworksData, setAllNetworksData] = useState<NetworkAnalytics[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortMode, setSortMode] = useState<'activity' | 'alphabetical'>('activity');

  const fetchNetworkAnalytics = async (chainId: number) => {
    try {
      const response = await fetch('/.netlify/functions/get-network-analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chainId }),
      });

      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error(`Error fetching analytics for chain ${chainId}:`, error);
      return null;
    }
  };

  const fetchAllNetworks = async () => {
    setLoading(true);
    try {
      const chainIds = NETWORKS.filter(n => n.chainId !== null).map(n => n.chainId!);
      const results = await Promise.all(chainIds.map(fetchNetworkAnalytics));
      const validResults = results.filter(r => r !== null) as NetworkAnalytics[];
      setAllNetworksData(validResults);

      // Calculate totals
      const totals: NetworkAnalytics = {
        chainId: 0,
        networkName: 'All Networks',
        symbol: 'Multi',
        totalTokens: validResults.reduce((sum, r) => sum + r.totalTokens, 0),
        tokensLast30Days: validResults.reduce((sum, r) => sum + r.tokensLast30Days, 0),
        activeUsers: validResults.reduce((sum, r) => sum + r.activeUsers, 0),
        platformFee: '0',
        revenue: validResults.reduce((sum, r) => sum + parseFloat(r.revenue), 0).toFixed(4),
      };
      setAnalytics(totals);
    } catch (error) {
      console.error('Error fetching all networks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleNetwork = async (chainId: number) => {
    setLoading(true);
    try {
      const data = await fetchNetworkAnalytics(chainId);
      if (data) {
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching network:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedNetwork === null) {
      fetchAllNetworks();
    } else {
      fetchSingleNetwork(selectedNetwork);
    }
  }, [selectedNetwork]);

  const handleRefresh = () => {
    if (selectedNetwork === null) {
      fetchAllNetworks();
    } else {
      fetchSingleNetwork(selectedNetwork);
    }
  };

  return (
    <div className="space-y-4">
      {/* Network Selector */}
      <div className="flex gap-2 items-center">
        <Select
          value={selectedNetwork === null ? '0' : selectedNetwork.toString()}
          onValueChange={(value) => setSelectedNetwork(value === '0' ? null : parseInt(value))}
        >
          <SelectTrigger className="w-[250px]">
            <SelectValue placeholder="Select Network" />
          </SelectTrigger>
          <SelectContent>
            {NETWORKS.map((network) => (
              <SelectItem key={network.id} value={network.id.toString()}>
                {network.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Tokens</CardTitle>
              <Coins className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.totalTokens}</div>
              <p className="text-xs text-muted-foreground">
                {analytics.networkName}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Last 30 Days</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.tokensLast30Days}</div>
              <p className="text-xs text-muted-foreground">
                New tokens created
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.activeUsers}</div>
              <p className="text-xs text-muted-foreground">
                Unique creators (30 days)
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Platform Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {parseFloat(analytics.revenue).toFixed(4)} {analytics.symbol}
              </div>
              <p className="text-xs text-muted-foreground">
                Fees collected (30 days)
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Network Breakdown Table (when All Networks is selected) */}
      {selectedNetwork === null && allNetworksData.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>Network Breakdown</CardTitle>
            <Select value={sortMode} onValueChange={(value: 'activity' | 'alphabetical') => setSortMode(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="activity">Sort by Activity</SelectItem>
                <SelectItem value="alphabetical">Sort Alphabetically</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Network</th>
                    <th className="text-right p-2">Total Tokens</th>
                    <th className="text-right p-2">30 Days</th>
                    <th className="text-right p-2">Active Users</th>
                    <th className="text-right p-2">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {allNetworksData
                    .sort((a, b) => {
                      if (sortMode === 'alphabetical') {
                        return a.networkName.localeCompare(b.networkName);
                      }
                      return b.totalTokens - a.totalTokens;
                    })
                    .map((network) => (
                      <tr key={network.chainId} className="border-b hover:bg-muted/50">
                        <td className="p-2 font-medium">{network.networkName}</td>
                        <td className="text-right p-2">{network.totalTokens}</td>
                        <td className="text-right p-2">{network.tokensLast30Days}</td>
                        <td className="text-right p-2">{network.activeUsers}</td>
                        <td className="text-right p-2">
                          {parseFloat(network.revenue).toFixed(4)} {network.symbol}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
